import { supabase } from '../supabase'
import { fetchAllPaged, fetchByIdsChunked } from '../pagination'
import { displayDate } from '../../utils/dates'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string) => (supabase as any).from(t)

export interface CustomerPaymentFilters {
  from: string        // yyyy-MM-dd inclusive
  to: string          // yyyy-MM-dd inclusive
  search: string      // matches customer name or note
  showVoided: boolean // include voided rows (struck-through); totals always exclude them
  // Header filters (Feature 4). Empty/undefined = no filter, never "match nothing".
  customerIds?: string[]           // .in('customer_id', …)
  methods?: string[]               // .in('payment_method', …)
  statuses?: string[]              // subset of ['posted','detached','voided']
  amountMin?: number | null
  amountMax?: number | null
}

export interface CustomerPaymentRow {
  id: string
  date: string
  customerName: string
  method: string | null
  amount: number
  saleRef: string | null       // sales_orders.order_number (order_id has no FK — resolved separately)
  journalId: string | null
  journalNumber: string | null
  isVoided: boolean
  isDetached: boolean
}

// journal_id / is_voided exist on the live table (mig 231) but not in the stale
// generated types; order_id is polymorphic (sale|purchase) with NO FK, so
// order_number and the journal entry_number are resolved via id-lookups, not embeds.
const PAGE_SELECT =
  'id, paid_on, amount, payment_method, note, order_id, customer_id, ' +
  'is_voided, is_detached, journal_id, customers(name)'

function sanitizeTerm(term: string): string {
  return term.replace(/[(),]/g, ' ').trim()
}

async function resolveCustomerIds(tid: string, term: string): Promise<string[] | null> {
  if (!term) return null
  const { data } = await db('customers')
    .select('id')
    .eq('tenant_id', tid)
    .ilike('name', `%${sanitizeTerm(term)}%`)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((data ?? []) as any[]).map(r => r.id as string)
}

// Translate the derived Status multi-select into an OR of underlying predicates.
// Returns null when the filter is inactive (empty, or all three states selected).
function customerStatusOr(statuses: string[] | undefined): string | null {
  if (!statuses || !statuses.length) return null
  const all = ['posted', 'detached', 'voided']
  if (all.every(s => statuses.includes(s))) return null
  const parts: string[] = []
  for (const s of statuses) {
    if (s === 'posted') parts.push('and(is_voided.not.is.true,is_detached.not.is.true)')
    else if (s === 'detached') parts.push('and(is_voided.not.is.true,is_detached.is.true)')
    else if (s === 'voided') parts.push('is_voided.is.true')
  }
  return parts.length ? parts.join(',') : null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyCustomerFilters(q: any, tid: string, filters: CustomerPaymentFilters, customerIds: string[] | null) {
  q = q
    .eq('tenant_id', tid)
    .eq('order_type', 'sale')
    .gte('paid_on', filters.from)
    .lte('paid_on', filters.to)
  if (!filters.showVoided) q = q.not('is_voided', 'is', true)
  if (filters.search) {
    const term = sanitizeTerm(filters.search)
    const ids = customerIds ?? []
    if (ids.length) q = q.or(`note.ilike.%${term}%,customer_id.in.(${ids.join(',')})`)
    else q = q.ilike('note', `%${term}%`)
  }
  // Header filters (AND with everything above). Empty arrays are skipped.
  if (filters.customerIds?.length) q = q.in('customer_id', filters.customerIds)
  if (filters.methods?.length) q = q.in('payment_method', filters.methods)
  if (filters.amountMin != null) q = q.gte('amount', filters.amountMin)
  if (filters.amountMax != null) q = q.lte('amount', filters.amountMax)
  const statusOr = customerStatusOr(filters.statuses)
  if (statusOr) q = q.or(statusOr)
  return q
}

/** Resolve sales_orders.order_number and journal_entries.entry_number for a set of rows. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function buildRefMaps(rows: any[]): Promise<{ orders: Map<string, string>; journals: Map<string, string> }> {
  const orderIds = [...new Set(rows.map(r => r.order_id).filter(Boolean) as string[])]
  const journalIds = [...new Set(rows.map(r => r.journal_id).filter(Boolean) as string[])]

  const orderRows = orderIds.length
    ? await fetchByIdsChunked<{ id: string; order_number: string }>(orderIds, batch =>
        db('sales_orders').select('id, order_number').in('id', batch))
    : []
  const journalRows = journalIds.length
    ? await fetchByIdsChunked<{ id: string; entry_number: string }>(journalIds, batch =>
        db('journal_entries').select('id, entry_number').in('id', batch))
    : []

  return {
    orders: new Map(orderRows.map(o => [o.id, o.order_number])),
    journals: new Map(journalRows.map(j => [j.id, j.entry_number])),
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapRow(r: any, orders: Map<string, string>, journals: Map<string, string>): CustomerPaymentRow {
  return {
    id: r.id,
    date: r.paid_on,
    customerName: r.customers?.name ?? '—',
    method: r.payment_method ?? null,
    amount: Number(r.amount ?? 0),
    saleRef: r.order_id ? orders.get(r.order_id) ?? null : null,
    journalId: r.journal_id ?? null,
    journalNumber: r.journal_id ? journals.get(r.journal_id) ?? null : null,
    isVoided: !!r.is_voided,
    isDetached: r.is_detached === true,
  }
}

/** One page of customer payments (one row per method-line) + exact filtered count. */
export async function fetchCustomerPaymentsPage(
  tid: string,
  filters: CustomerPaymentFilters,
  { page, pageSize }: { page: number; pageSize: number },
): Promise<{ rows: CustomerPaymentRow[]; count: number }> {
  const customerIds = await resolveCustomerIds(tid, filters.search)
  let q = db('order_payments').select(PAGE_SELECT, { count: 'exact' })
  q = applyCustomerFilters(q, tid, filters, customerIds)
  const { data, count, error } = await q
    .order('paid_on', { ascending: false })
    .order('id', { ascending: false })
    .range((page - 1) * pageSize, page * pageSize - 1)
  if (error) throw error
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = (data ?? []) as any[]
  const { orders, journals } = await buildRefMaps(raw)
  return { rows: raw.map(r => mapRow(r, orders, journals)), count: count ?? 0 }
}

/** Footer sum across ALL filtered rows; voided always excluded (detached counts — decision #1). */
export async function fetchCustomerPaymentsTotal(tid: string, filters: CustomerPaymentFilters): Promise<number> {
  const customerIds = await resolveCustomerIds(tid, filters.search)
  const rows = await fetchAllPaged<{ amount: number; is_voided: boolean }>((from, to) =>
    applyCustomerFilters(db('order_payments').select('amount, is_voided'), tid, filters, customerIds)
      .order('id', { ascending: true })
      .range(from, to),
  )
  return rows.filter(r => !r.is_voided).reduce((s, r) => s + Number(r.amount ?? 0), 0)
}

export interface CustomerPaymentExportRow extends Record<string, unknown> {
  date: string
  customer: string
  method: string
  amount: number
  sale_reference: string
  journal: string
  status: string
}

function statusLabel(r: CustomerPaymentRow): string {
  if (r.isVoided) return 'Voided'
  if (r.isDetached) return 'Detached'
  return 'Posted'
}

export function toCustomerExportRow(r: CustomerPaymentRow): CustomerPaymentExportRow {
  return {
    date: displayDate(r.date),
    customer: r.customerName,
    method: r.method ?? '',
    amount: r.amount,
    sale_reference: r.saleRef ?? '',
    journal: r.journalNumber ?? r.journalId ?? '',
    status: statusLabel(r),
  }
}

/** All filtered rows, mapped for CSV/Excel export (decision #8: every matching row). */
export async function fetchCustomerPaymentsExport(tid: string, filters: CustomerPaymentFilters): Promise<CustomerPaymentExportRow[]> {
  const customerIds = await resolveCustomerIds(tid, filters.search)
  const rows = await fetchAllPaged<Record<string, unknown>>((from, to) =>
    applyCustomerFilters(db('order_payments').select(PAGE_SELECT), tid, filters, customerIds)
      .order('paid_on', { ascending: false })
      .order('id', { ascending: false })
      .range(from, to),
  )
  const { orders, journals } = await buildRefMaps(rows)
  return rows.map(r => mapRow(r, orders, journals)).map(toCustomerExportRow)
}
